import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Chip,
} from "@mui/material";
import {
  IconX,
  IconMapPin,
  IconLeaf,
  IconSortDescending,
  IconAdjustmentsHorizontal,
  IconRotateClockwise2,
} from "@tabler/icons-react";
import { GetState, GetCity } from "react-country-state-city";
import { colors, fonts } from "../../ColorFont/color";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

const PRIMARY = colors.primary;
const TEXT_DARK = "#1C252E";
const TEXT_SECONDARY = "#637381";

const FilterDialog = ({
  open,
  onClose,
  onClear,
  onApply,
  // State values
  state,
  setState,
  tehsil,
  setTehsil,
  stateid,
  setstateid,
  countryid = 101,
  selectedCategory,
  setSelectedCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  categories = [],
  subCategories = [],
  filterStatus,
  setFilterStatus,
  ownPlots,
  setOwnPlots,
  sortBy,
  setSortBy,
  // Visibility toggles
  showOwnPlots = true,
  showStatus = true,
  showLocation = true,
  showCategory = true,
  showSort = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);

  // Fetch States on mount
  useEffect(() => {
    GetState(countryid).then((result) => {
      setAllStates(result);
    });
  }, [countryid]);

  // Fetch Cities when stateid changes
  useEffect(() => {
    if (stateid) {
      GetCity(countryid, stateid).then((result) => {
        setAllCities(result);
      });
    } else {
      setAllCities([]);
    }
  }, [countryid, stateid]);

  const sectionTitleStyle = {
    fontFamily: fonts.poppins,
    fontWeight: 700,
    fontSize: "14px",
    color: TEXT_DARK,
    mb: 2,
    display: "flex",
    alignItems: "center",
    gap: 1,
  };

  const formControlStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#FFFFFF",
      fontFamily: fonts.poppins,
      fontSize: "14px",
      "& fieldset": { borderColor: "#E0E5EB" },
      "&:hover fieldset": { borderColor: "#919EAB" },
      "&.Mui-focused fieldset": { borderColor: PRIMARY },
    },
    "& .MuiInputLabel-root": {
      fontFamily: fonts.poppins,
      fontSize: "14px",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      transitionDuration={300}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          width: "100%",
          maxWidth: "600px",
          m: 2,
          boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.16)",
        },
      }}
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #F4F6F8",
        }}
      >
        <Typography
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 800,
            fontSize: "20px",
            color: TEXT_DARK,
          }}
        >
          {t("filter")}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: TEXT_SECONDARY }}
        >
          <IconX size={20} stroke={2.5} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3, pb: 2 }}>
        <Grid container spacing={4}>
          {/* Location Section */}
          {showLocation && (
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography sx={sectionTitleStyle}>
                <IconMapPin size={18} color={PRIMARY} /> {t("location")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={formControlStyle}>
                    <Select
                      value={stateid || ""}
                      displayEmpty
                      onChange={(e) => {
                        const selectedState = allStates.find(
                          (s) => s.id === e.target.value,
                        );
                        if (selectedState) {
                          setState(selectedState.name);
                          setstateid(selectedState.id);
                          setTehsil(""); // Reset tehsil when state changes
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected)
                          return (
                            <Typography
                              sx={{ color: "#919EAB", fontSize: "14px" }}
                            >
                              {t("selectState")}
                            </Typography>
                          );
                        return allStates.find((s) => s.id === selected)?.name;
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t("selectState")}
                      </MenuItem>
                      {allStates.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={formControlStyle}
                    disabled={!stateid}
                  >
                    <Select
                      value={tehsil || ""}
                      displayEmpty
                      onChange={(e) => setTehsil(e.target.value)}
                      renderValue={(selected) => {
                        if (!selected)
                          return (
                            <Typography
                              sx={{ color: "#919EAB", fontSize: "14px" }}
                            >
                              {stateid
                                ? t("selectTehsil")
                                : t("selectStateFirst")}
                            </Typography>
                          );
                        return selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t("selectTehsil")}
                      </MenuItem>
                      {allCities.map((city) => (
                        <MenuItem key={city.id} value={city.name}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Category Section */}
          {showCategory && (
            <Grid item xs={12}>
              <Typography sx={sectionTitleStyle}>
                <IconLeaf size={18} color={PRIMARY} /> {t("category")}
              </Typography>

              {/* Categories Chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                <Chip
                  label={t("common:all")}
                  onClick={() => {
                    setSelectedCategory("");
                    setSelectedSubCategory("");
                  }}
                  sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 600,
                    backgroundColor:
                      selectedCategory === "" ? PRIMARY : "#F4F6F8",
                    color: selectedCategory === "" ? "#FFF" : TEXT_DARK,
                    "&:hover": {
                      backgroundColor:
                        selectedCategory === ""
                          ? colors.primary_dark
                          : "#E0E5EB",
                    },
                  }}
                />
                {categories.map((cat) => (
                  <Chip
                    key={cat._id}
                    label={t(`common:categories.${cat.categoryType}`)}
                    onClick={() => {
                      setSelectedCategory(cat.categoryType);
                      setSelectedSubCategory("");
                    }}
                    sx={{
                      fontFamily: fonts.poppins,
                      fontWeight: 600,
                      backgroundColor:
                        selectedCategory === cat.categoryType
                          ? PRIMARY
                          : "#F4F6F8",
                      color:
                        selectedCategory === cat.categoryType
                          ? "#FFF"
                          : TEXT_DARK,
                      "&:hover": {
                        backgroundColor:
                          selectedCategory === cat.categoryType
                            ? colors.primary_dark
                            : "#E0E5EB",
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Subcategories Chips (only shown if a category is selected) */}
              {selectedCategory && subCategories.length > 0 && (
                <>
                  <Typography
                    sx={{
                      ...sectionTitleStyle,
                      fontSize: "13px",
                      color: TEXT_SECONDARY,
                      mb: 1.5,
                    }}
                  >
                    {t("subCategory")}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {subCategories.map((sub, idx) => (
                      <Chip
                        key={idx}
                        label={t(`common:crops.${sub.replace('crops.', '')}`).replace('crops.', '')}
                        size="small"
                        onClick={() =>
                          setSelectedSubCategory(
                            sub === selectedSubCategory ? "" : sub,
                          )
                        }
                        sx={{
                          fontFamily: fonts.poppins,
                          fontWeight: 500,
                          backgroundColor:
                            selectedSubCategory === sub
                              ? PRIMARY
                              : "transparent",
                          color:
                            selectedSubCategory === sub
                              ? "#FFF"
                              : TEXT_SECONDARY,
                          border: `1px solid ${selectedSubCategory === sub ? PRIMARY : "#E0E5EB"}`,
                          "&:hover": {
                            backgroundColor:
                              selectedSubCategory === sub
                                ? colors.primary_dark
                                : "#F4F6F8",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Grid>
          )}

          {/* Options Section */}
          {(showStatus || showSort) && (
            <Grid item xs={12}>
              <Typography sx={sectionTitleStyle}>
                <IconAdjustmentsHorizontal size={18} color={PRIMARY} />{" "}
                {t("options")}
              </Typography>
              <Grid container spacing={2}>
                {showStatus && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={formControlStyle}>
                      <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <Typography
                                sx={{ color: "#919EAB", fontSize: "14px" }}
                              >
                                {t("selectStatus")}
                              </Typography>
                            );
                          return selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t("selectStatus")}
                        </MenuItem>
                        <MenuItem value="Available">{t("Available")}</MenuItem>
                        <MenuItem value="Sold Out">{t("Sold Out")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {showSort && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={formControlStyle}>
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <Typography
                                sx={{ color: "#919EAB", fontSize: "14px" }}
                              >
                                {t("sortBy")}
                              </Typography>
                            );
                          return selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t("defaultSort")}
                        </MenuItem>
                        <MenuItem value="Latest First">
                          {t("Latest First")}
                        </MenuItem>
                        <MenuItem value="Price: Low to High">
                          {t("Price: Low to High")}
                        </MenuItem>
                        <MenuItem value="Price: High to Low">
                          {t("Price: High to Low")}
                        </MenuItem>
                        <MenuItem value="Stock: High to Low">
                          {t("Stock: High to Low")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}

          {/* Checkbox Section */}
          {showOwnPlots && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ownPlots}
                    onChange={(e) => setOwnPlots(e.target.checked)}
                    sx={{ color: PRIMARY, "&.Mui-checked": { color: PRIMARY } }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("viewMyPlotsOnly")}
                  </Typography>
                }
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <Divider sx={{ borderColor: "#F4F6F8" }} />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClear}
          startIcon={<IconRotateClockwise2 size={18} />}
          sx={{
            color: TEXT_SECONDARY,
            textTransform: "none",
            fontFamily: fonts.poppins,
            fontWeight: 600,
            "&:hover": { backgroundColor: "#F4F6F8" },
          }}
        >
          {t("reset")}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            onApply();
            onClose();
          }}
          sx={{
            backgroundColor: PRIMARY,
            color: "#FFF",
            borderRadius: "12px",
            textTransform: "none",
            fontFamily: fonts.poppins,
            fontWeight: 700,
            fontSize: "15px",
            py: 1.2,
            boxShadow: "0px 8px 16px colors.primary_rgba_24",
            "&:hover": {
              backgroundColor: colors.primary_dark || "#149343",
              boxShadow: "0px 12px 24px rgba(22, 162, 73, 0.32)",
            },
          }}
        >
          {t("showResults")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterDialog;
